import {
  CanvasDimensions,
  getZonesByTypeAsCameraFormat,
  ImageResolution,
  SpecialZoneType,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';
import { useUpdateCameraZones } from '@hakimo-ui/hakimo/data-access';
import { Point as CameraPoint } from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { useCallback } from 'react';

interface UseZoneMutationsParams {
  cameraId: string;
}

export function useZoneMutations({ cameraId }: UseZoneMutationsParams) {
  const updateDeadZoneMutation = useUpdateCameraZones(
    cameraId,
    'deadzone',
    () => toast('Dead zones updated', { type: 'success' })
  );

  const updateVehicleParkingZoneMutation = useUpdateCameraZones(
    cameraId,
    'vehicleparkingzone',
    () => toast('Vehicle parking zones updated', { type: 'success' })
  );

  const updateActiveZoneMutation = useUpdateCameraZones(
    cameraId,
    'activezone',
    () => toast('Active zones updated', { type: 'success' })
  );

  const saveZonesByType = useCallback(
    (
      zones: ZonePolygonData[],
      type: SpecialZoneType,
      canvasDimensions: CanvasDimensions,
      imageResolution: ImageResolution,
      onSuccess?: () => void
    ) => {
      const labellingResolution: CameraPoint = [
        imageResolution.width,
        imageResolution.height,
      ];

      const zonesData = getZonesByTypeAsCameraFormat(
        zones,
        type,
        canvasDimensions,
        imageResolution
      );

      const mutateOptions = onSuccess ? { onSuccess } : undefined;

      switch (type) {
        case SpecialZoneType.DeadZone:
          updateDeadZoneMutation.mutate(
            { deadZones: { deadZones: zonesData, labellingResolution } },
            mutateOptions
          );
          break;
        case SpecialZoneType.VehicleParkingZone:
          updateVehicleParkingZoneMutation.mutate(
            {
              vehicleParkingZones: {
                vehicleParkingZones: zonesData,
                labellingResolution,
              },
            },
            mutateOptions
          );
          break;
        case SpecialZoneType.ActiveZone:
          updateActiveZoneMutation.mutate(
            { activeZones: { activeZones: zonesData, labellingResolution } },
            mutateOptions
          );
          break;
      }
    },
    [
      updateDeadZoneMutation,
      updateVehicleParkingZoneMutation,
      updateActiveZoneMutation,
    ]
  );

  const isLoading =
    updateDeadZoneMutation.isLoading ||
    updateVehicleParkingZoneMutation.isLoading ||
    updateActiveZoneMutation.isLoading;

  return { saveZonesByType, isSaving: isLoading, isDeleting: isLoading };
}
